/**
 * examResume — herstel van een toetssessie na het sluiten van de browser.
 *
 * WAAROM (audit blok 5, 02-09-2026)
 *   In de pincode-flow leefde het sessie-token alleen in sessionStorage. Ging
 *   de batterij leeg of klapte de laptop dicht (browser afgesloten), dan was
 *   het token weg: de leerling kon niet meer terug in dezelfde sessie (409
 *   SESSION_ALREADY_ACTIVE) en begon van nul, tot de docent hem kickte.
 *
 *   Dit laagje bewaart in localStorage (overleeft browser-herstart op hetzelfde
 *   toestel): het resume-token per sessie én per (toetscode, naam), plus een
 *   kopie van de ingevulde antwoorden. Zo komt de leerling terug waar hij was.
 *   De server geeft een actieve sessie alleen terug bij een KLOPPEND resume-
 *   token (examSessionRoutes: anders 409), dus dit token is de sleutel tot
 *   precies de eigen sessie, niet meer.
 *
 *   Alles is best-effort: geen localStorage (privémodus, geblokkeerd) → stil
 *   terugvallen op het oude gedrag. Records worden bij inleveren gewist.
 *
 * HOE LANG (Bijlage 1 D.1 "Inloggen", bouwlijst A3, 22-10-2026)
 *   De kopie op het apparaat leeft tot het einde van de toetstijd: bewaarSessie
 *   krijgt `verlooptOp` (ms) mee en de antwoorden en audiotellers erven dat
 *   moment. Is er geen toetstijd bekend (communicatiesessie zonder limiet), dan
 *   geldt TTL_MS: 4 uur, net zo lang als de server een sessie laat leven.
 */

const PREFIX = 'tw-exam-resume:';
const TTL_MS = 4 * 60 * 60 * 1000; // zonder bekend einde van de toetstijd: 4 uur, zoals de server

function veilig(fn, terug = null) {
  try { return fn(); } catch (_) { return terug; }
}
function normCode(code) { return String(code || '').trim(); }
function normNaam(naam) { return String(naam || '').trim().toLowerCase(); }
function codeSleutel(code, naam) { return `${PREFIX}code:${normCode(code)}|${normNaam(naam)}`; }
function sessieSleutel(sessionId) { return `${PREFIX}sessie:${sessionId}`; }

function schrijf(sleutel, obj, tot) {
  const record = { ...obj, _t: Date.now() };
  if (Number.isFinite(tot)) record._tot = tot;
  veilig(() => localStorage.setItem(sleutel, JSON.stringify(record)));
}
function verlopen(obj) {
  if (!obj || typeof obj._t !== 'number') return true;
  if (typeof obj._tot === 'number') return Date.now() > obj._tot;
  return Date.now() - obj._t > TTL_MS;
}
function lees(sleutel) {
  return veilig(() => {
    const raw = localStorage.getItem(sleutel);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (verlopen(obj)) {
      veilig(() => localStorage.removeItem(sleutel));
      return null;
    }
    return obj;
  });
}
/** Einde van de toetstijd zoals bij de sessie bewaard; undefined als onbekend. */
function totVanSessie(sessionId) {
  const obj = lees(sessieSleutel(sessionId));
  return obj && typeof obj._tot === 'number' ? obj._tot : undefined;
}

/**
 * Bewaar een gestarte sessie zodat de leerling na browser-sluiting terugkomt.
 * `verlooptOp` (ms sinds epoch) is het einde van de toetstijd; zonder dat
 * verloopt het record na TTL_MS.
 */
export function bewaarSessie({ sessionId, sessionToken, toetsCode, leerlingNaam, verlooptOp }) {
  if (!sessionId || !sessionToken) return;
  const tot = Number.isFinite(verlooptOp) ? verlooptOp : undefined;
  schrijf(sessieSleutel(sessionId), { sessionId, sessionToken }, tot);
  if (toetsCode && leerlingNaam) {
    schrijf(codeSleutel(toetsCode, leerlingNaam), { sessionId, sessionToken }, tot);
  }
}

/** Token voor een bekende sessie-id (na refresh of navigatie). */
export function tokenBijSessie(sessionId) {
  const obj = lees(sessieSleutel(sessionId));
  return obj?.sessionToken || null;
}

/** Sessie + token voor (toetscode, naam), voor het opnieuw inloggen na sluiten. */
export function sessieBijCode(toetsCode, leerlingNaam) {
  const obj = lees(codeSleutel(toetsCode, leerlingNaam));
  return obj && obj.sessionId && obj.sessionToken
    ? { sessionId: obj.sessionId, sessionToken: obj.sessionToken }
    : null;
}

/** Bewaar de ingevulde antwoorden (de server heeft ze pas na een autosave). */
export function bewaarAntwoorden(sessionId, answers) {
  if (!sessionId || !answers) return;
  schrijf(`${sessieSleutel(sessionId)}:antw`, { answers }, totVanSessie(sessionId));
}

/** Lokaal bewaarde antwoorden terug (kan verser zijn dan de server). */
export function antwoordenBijSessie(sessionId) {
  const obj = lees(`${sessieSleutel(sessionId)}:antw`);
  return obj?.answers || null;
}

/**
 * Afspeeltellers van luisterfragmenten (07-10-2026). Zonder dit gaf een
 * herlaadbeurt (F5) de leerling een verse teller en was "maximaal 2x
 * afspelen" niet waar. Sleutel = audio-url (gedeeld binnen een groep).
 */
export function bewaarAudioTellers(sessionId, tellers) {
  if (!sessionId || !tellers) return;
  schrijf(`${sessieSleutel(sessionId)}:audio`, { tellers }, totVanSessie(sessionId));
}

export function audioTellersBijSessie(sessionId) {
  const obj = lees(`${sessieSleutel(sessionId)}:audio`);
  return obj && obj.tellers && typeof obj.tellers === 'object' ? obj.tellers : null;
}

/** Alles voor een sessie wissen (bij inleveren). */
export function wis(sessionId, toetsCode, leerlingNaam) {
  veilig(() => localStorage.removeItem(sessieSleutel(sessionId)));
  veilig(() => localStorage.removeItem(`${sessieSleutel(sessionId)}:antw`));
  veilig(() => localStorage.removeItem(`${sessieSleutel(sessionId)}:audio`));
  if (toetsCode && leerlingNaam) veilig(() => localStorage.removeItem(codeSleutel(toetsCode, leerlingNaam)));
}
